import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Send, CheckCircle2, Clock, User2, Settings2, UserCheck } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ConfirmModal } from '@/components/ConfirmModal';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/toast';
import { formatDateTime } from '@/lib/dateFormat';
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  CATEGORIES,
  SCOPE_LABELS,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  humanStatus,
  type Ticket,
  type ThreadMessage,
  type TicketStatus,
  type TicketPriority,
} from './supportCommon';
import { SelectField } from '@/components/ui/SelectField';

interface TenantUser { id: string; name: string; role: string; }

export function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get(`/support/tickets/${id}`),
        api.get(`/support/tickets/${id}/messages`),
      ]);
      setTicket(t.data);
      setMessages(Array.isArray(m.data) ? m.data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load ticket');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!ticket || ticket.scope !== 'tenant_internal' || !isManager) return;
    api.get('/users', { params: { limit: 100 } })
      .then((res) => {
        const list: TenantUser[] = (res.data?.data ?? res.data?.users ?? res.data ?? [])
          .filter((u: any) => u.role === 'admin' || u.role === 'manager')
          .map((u: any) => ({ id: u.id, name: u.name, role: u.role }));
        setAssignableUsers(list);
      })
      .catch(() => {});
  }, [ticket, isManager]);

  const postReply = async () => {
    if (!reply.trim() || !ticket) return;
    setPosting(true);
    try {
      await api.post(`/support/tickets/${ticket.id}/messages`, { body: reply });
      setReply('');
      await load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to send reply');
    } finally {
      setPosting(false);
    }
  };

  const closeTicket = async () => {
    if (!ticket) return;
    setClosing(true);
    try {
      await api.patch(`/support/tickets/${ticket.id}/close`);
      toast('success', 'Ticket closed');
      setClosingOpen(false);
      await load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to close');
    } finally {
      setClosing(false);
    }
  };

  const updateMeta = async (patch: { status?: TicketStatus; priority?: TicketPriority; assignedTenantUserId?: string | null; assignedTenantUserName?: string | null }) => {
    if (!ticket) return;
    setSavingMeta(true);
    try {
      await api.patch(`/support/tickets/${ticket.id}/manage`, patch);
      await load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update');
    } finally {
      setSavingMeta(false);
    }
  };

  if (loading || !ticket) {
    return (
      <DashboardLayout>
        <div className="p-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#6366F1]" />
        </div>
      </DashboardLayout>
    );
  }

  const canReply = ticket.status !== 'closed';
  const scope = SCOPE_LABELS[ticket.scope] ?? SCOPE_LABELS.platform;
  const showSidebar = ticket.scope === 'tenant_internal' && isManager;

  // ── Reply composer (shared between open / closed states) ───────────────────
  const replyComposer = canReply ? (
    <div className="px-5 py-4 border-t border-gray-50 bg-slate-50/40">
      <textarea
        rows={4}
        value={reply}
        onChange={(e) => setReply(e.target.value)}
        placeholder="Type your reply…"
        className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none placeholder:text-slate-400"
        maxLength={10000}
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-slate-400">{reply.length} / 10000</span>
        <div className="flex items-center gap-2">
          {ticket.status !== 'resolved' && (
            <button onClick={() => setClosingOpen(true)} className="text-xs font-medium text-slate-500 hover:text-red-500 px-2 py-1">
              Close ticket
            </button>
          )}
          <button
            onClick={postReply}
            disabled={posting || !reply.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
            {posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Send Reply
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className="px-5 py-6 border-t border-gray-50 bg-emerald-50/40 text-center">
      <CheckCircle2 className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
      <p className="text-sm text-slate-700 font-medium">This ticket is closed</p>
      <p className="text-xs text-slate-500 mt-1">Reply below to re-open it.</p>
      <div className="mt-3 max-w-2xl mx-auto">
        <textarea
          rows={3}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply to re-open…"
          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] resize-none"
        />
        <button
          onClick={postReply}
          disabled={posting || !reply.trim()}
          className="mt-2 px-4 py-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold disabled:opacity-60">
          Reply & Re-open
        </button>
      </div>
    </div>
  );

  // ── Main content blocks ───────────────────────────────────────────────────
  const headerCard = (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-slate-900 break-words">{ticket.subject}</h1>
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-mono">#{ticket.id.slice(-6).toUpperCase()}</span>
            {' · '}Submitted {formatDateTime(ticket.createdAt)}
            {ticket.userName && ` by ${ticket.userName}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${scope.color}`}>{scope.short}</span>
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${STATUS_COLORS[ticket.status]}`}>
            {humanStatus(ticket.status)}
          </span>
          <span className={`text-[10px] font-medium uppercase px-2 py-1 rounded-full ${PRIORITY_COLORS[ticket.priority]}`}>
            {ticket.priority}
          </span>
          <span className="text-[10px] font-medium uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-600">
            {CATEGORIES.find((c) => c.v === ticket.category)?.l ?? ticket.category}
          </span>
        </div>
      </div>
    </div>
  );

  const conversationCard = (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-50">
        <span className="text-sm font-medium text-slate-700">Conversation</span>
      </div>
      <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
        <Message author={ticket.userName ?? 'You'} type="tenant" body={ticket.message} at={ticket.createdAt} />
        {messages.map((m) => (
          <Message
            key={m.id}
            author={m.authorName ?? (m.authorType === 'admin' ? 'BrixLead Support' : 'You')}
            type={m.authorType}
            body={m.body}
            at={m.createdAt}
          />
        ))}
      </div>
      {replyComposer}
    </div>
  );

  const sidebar = showSidebar ? (
    <div className="space-y-4">
      <SidebarCard title="Manage Ticket">
        <SidebarLabel>Status</SidebarLabel>
        <SelectField
          value={ticket.status}
          onChange={(e) => updateMeta({ status: e.target.value as TicketStatus })}
          disabled={savingMeta}
          className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{humanStatus(s)}</option>)}
        </SelectField>

        <SidebarLabel className="mt-3">Priority</SidebarLabel>
        <SelectField
          value={ticket.priority}
          onChange={(e) => updateMeta({ priority: e.target.value as TicketPriority })}
          disabled={savingMeta}
          className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
          {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </SelectField>
      </SidebarCard>

      <SidebarCard title="Assigned To">
        <SelectField
          value={ticket.assignedTenantUserId ?? ''}
          onChange={(e) => {
            const uid = e.target.value || null;
            const uname = uid ? assignableUsers.find((u) => u.id === uid)?.name ?? null : null;
            updateMeta({ assignedTenantUserId: uid, assignedTenantUserName: uname });
          }}
          disabled={savingMeta}
          className="w-full h-9 px-2.5 rounded-lg border border-gray-200 text-xs bg-white">
          <option value="">— Unassigned —</option>
          {user?.id && (
            <option value={user.id}>
              👤 Assign to me ({user.name})
            </option>
          )}
          {assignableUsers.filter((u) => u.id !== user?.id).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({u.role})
            </option>
          ))}
        </SelectField>
        {ticket.assignedTenantUserName && (
          <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
            <UserCheck className="h-3 w-3" />
            {ticket.assignedTenantUserName}
            {ticket.assignedTenantUserId === user?.id && <span className="font-bold ml-1">(YOU)</span>}
          </p>
        )}
      </SidebarCard>

      <SidebarCard title="Timeline">
        <SidebarRow label="Created">{formatDateTime(ticket.createdAt)}</SidebarRow>
        <SidebarRow label="Updated">{formatDateTime(ticket.updatedAt)}</SidebarRow>
        {ticket.closedAt && <SidebarRow label="Closed">{formatDateTime(ticket.closedAt)}</SidebarRow>}
      </SidebarCard>
    </div>
  ) : null;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <button
          onClick={() => {
            // Return to the same tab the ticket belongs to (platform vs
            // tenant_internal) so the list view re-opens in context.
            const tab = ticket.scope === 'platform' ? 'platform' : 'tenant_internal';
            navigate(`/support?tab=${tab}`);
          }}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="h-4 w-4" />
          Back to Support
        </button>

        {showSidebar ? (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
            <div>
              {headerCard}
              {conversationCard}
            </div>
            {sidebar}
          </div>
        ) : (
          <>
            {headerCard}
            {conversationCard}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={closingOpen}
        title="Close this ticket?"
        message="Mark this ticket as closed. You can re-open it by sending a new reply."
        confirmText="Close Ticket"
        confirmButtonColor="red"
        onConfirm={closeTicket}
        onCancel={() => setClosingOpen(false)}
        isLoading={closing}
      />
    </DashboardLayout>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────
function Message({
  author, type, body, at,
}: { author: string; type: 'tenant' | 'admin' | 'system'; body: string; at: string }) {
  if (type === 'system') {
    return (
      <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-400 italic">
        <Settings2 className="h-3 w-3" />
        <span>{body}</span>
        <span>·</span>
        <span><Clock className="inline h-3 w-3 mr-0.5" />{formatDateTime(at)}</span>
      </div>
    );
  }
  const isTenant = type === 'tenant';
  return (
    <div className={`flex gap-3 ${isTenant ? 'justify-end' : 'justify-start'}`}>
      {!isTenant && (
        <div className="h-8 w-8 rounded-full bg-emerald-100 flex-shrink-0 flex items-center justify-center">
          <User2 className="h-4 w-4 text-emerald-700" />
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
        isTenant
          ? 'bg-indigo-600 text-white rounded-tr-sm'
          : 'bg-emerald-50 border border-emerald-100 text-slate-800 rounded-tl-sm'
      }`}>
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <span className={`text-[11px] font-semibold ${isTenant ? 'text-indigo-100' : 'text-emerald-700'}`}>{author}</span>
          <span className={`text-[10px] ${isTenant ? 'text-indigo-200' : 'text-slate-400'} whitespace-nowrap`}>{formatDateTime(at)}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap break-words">{body}</p>
      </div>
      {isTenant && (
        <div className="h-8 w-8 rounded-full bg-indigo-100 flex-shrink-0 flex items-center justify-center">
          <User2 className="h-4 w-4 text-indigo-700" />
        </div>
      )}
    </div>
  );
}

function SidebarCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</p>
      {children}
    </div>
  );
}

function SidebarLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <p className={`text-[11px] font-medium text-slate-500 mb-1 ${className}`}>{children}</p>;
}

function SidebarRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 last:mb-0">
      <p className="text-[11px] font-medium text-slate-500 mb-0.5">{label}</p>
      <p className="text-xs text-slate-700">{children}</p>
    </div>
  );
}

export default SupportTicketDetailPage;
